import React, { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import IconAdd from '../../../../../assets/plus.png';
import SelectChute from '../../../../../components/molecules/PatternSelectChute/PatternSelectChute';
import { AppSelectSearchable } from 'components/atoms/AppSelectSearchable/AppSelectSearchable';
import { Chute } from 'interfaces/templates';
import { RouteChangeSelector } from '../../organisms/RouteChangeSelector/RouteChangeSelector';
import { DynamicIcon } from 'components/atoms/AppIcon';
import AppTooltip from 'components/atoms/AppTooltip/AppTooltip';

interface RouteChangeConfig {
  provider: { name: string; code: string } | null;
  providerService: { name: string; code: string } | null;
}

interface Props {
  /** Stable id used by @dnd-kit for drag-and-drop */
  dndId: string;
  /** 1-based display position shown in the number badge */
  position: number;
  /** Total number of rules, used to clamp input range */
  totalCount: number;
  chutes: Chute[];
  ruleOptions: { value: string; label: string }[];
  onDelete: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onChutesSelected: (ruleIndex: number, selected: number[]) => void;
  selectedChutes: number[];
  ruleIndex: number;
  onGroupUpdate: (
    ruleIndex: number,
    group_id: number,
    chutes: number[],
    providerServiceCode?: string,
  ) => void;
  onRouteChangeUpdate?: (config: RouteChangeConfig) => void;
  onRouteChangeLoadingChange?: (ruleIndex: number, loading: boolean) => void;
  onMaxCapacityChange?: (ruleIndex: number, value: number | null) => void;
  selectedRuleId?: string;
  defaultChutes?: { chute: number; id: number }[];
  disabledRuleIds?: number[];
  showChutes?: boolean;
  defaultRouteChangeConfig?: RouteChangeConfig;
  maxCapacity?: number | null;
  readOnly?: boolean;
}

interface ChuteList {
  chute: number;
  type: string;
  id?: number;
}

const PatternFormRuleItem = ({
  dndId,
  position,
  totalCount,
  chutes,
  ruleOptions,
  onDelete,
  onReorder,
  onChutesSelected,
  selectedChutes,
  ruleIndex,
  onGroupUpdate,
  onRouteChangeUpdate,
  onRouteChangeLoadingChange,
  onMaxCapacityChange,
  selectedRuleId,
  defaultChutes,
  disabledRuleIds = [],
  showChutes: showChutesSection = false,
  defaultRouteChangeConfig,
  maxCapacity,
  readOnly = false,
}: Props) => {
  // ── DnD sortable ──────────────────────────────────────────────────────────
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dndId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  // ── Local state ───────────────────────────────────────────────────────────
  const [showChuteModal, setShowChuteModal] = useState(false);
  const [chutesList, setChutesList] = useState<ChuteList[]>([]);
  const [selectedRule, setSelectedRule] = useState<string>(selectedRuleId || '');
  const [routeChangeValue, setRouteChangeValue] = useState<RouteChangeConfig>(
    defaultRouteChangeConfig || { provider: null, providerService: null },
  );
  const [capacityValue, setCapacityValue] = useState<string>(
    maxCapacity != null && maxCapacity > 0 ? String(maxCapacity) : '',
  );

  // Position editing state
  const [isEditingPosition, setIsEditingPosition] = useState(false);
  const [positionInput, setPositionInput] = useState(String(position));
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (defaultRouteChangeConfig) setRouteChangeValue(defaultRouteChangeConfig);
  }, [defaultRouteChangeConfig]);

  // Keep positionInput in sync when position changes externally (drag reorder)
  useEffect(() => {
    if (!isEditingPosition) setPositionInput(String(position));
  }, [position, isEditingPosition]);

  // Auto-focus the input when editing starts
  useEffect(() => {
    if (isEditingPosition) inputRef.current?.select();
  }, [isEditingPosition]);

  const commitPositionEdit = () => {
    const newPos = parseInt(positionInput, 10);
    if (!isNaN(newPos) && newPos >= 1 && newPos <= totalCount && newPos !== position) {
      onReorder(position - 1, newPos - 1); // convert to 0-based indices
    } else {
      setPositionInput(String(position)); // restore on invalid
    }
    setIsEditingPosition(false);
  };

  const cancelPositionEdit = () => {
    setPositionInput(String(position));
    setIsEditingPosition(false);
  };

  useEffect(() => {
    if (defaultChutes?.length && selectedRuleId && chutesList.length === 0) {
      const formatted = defaultChutes.map(ch => ({ chute: ch.chute, type: 'selected', id: ch.id }));
      setChutesList(formatted);
      onChutesSelected(ruleIndex, defaultChutes.map(ch => ch.chute));
      onGroupUpdate(
        ruleIndex,
        Number(selectedRuleId),
        defaultChutes.map(ch => ch.id),
        routeChangeValue.providerService?.code,
      );
    }
  }, [defaultChutes, selectedRuleId]);

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Solo permitir dígitos
    if (raw !== '' && !/^\d+$/.test(raw)) return;
    setCapacityValue(raw);
    const numVal = raw === '' ? null : Number(raw);
    if (onMaxCapacityChange) {
      onMaxCapacityChange(ruleIndex, numVal && numVal > 0 ? numVal : null);
    }
  };

  const handleRouteChange = (newValue: RouteChangeConfig) => {
    setRouteChangeValue(newValue);
    onRouteChangeUpdate?.(newValue);
  };

  const handleChutesChange = (selected: Chute[]) => {
    const formatted = selected.map(item => ({ chute: item.chute, type: 'selected', id: item.id }));
    setChutesList(formatted);
    onChutesSelected(ruleIndex, selected.map(item => item.chute));
    setShowChuteModal(false);
    const chuteIds = selected.map(item => item.id).filter((id): id is number => id !== undefined);
    onGroupUpdate(ruleIndex, Number(selectedRule), chuteIds, routeChangeValue.providerService?.code);
  };

  const handleRuleChange = (newRule: string) => {
    setSelectedRule(newRule);
    setChutesList([]);
    onChutesSelected(ruleIndex, []);
    onGroupUpdate(ruleIndex, Number(newRule), [], routeChangeValue.providerService?.code);
  };

  const canOpenSelector = selectedRule !== '';

  // Regla puede repetirse en varias filas: no se filtra ni se deshabilita.
  // Cada fila es una instancia independiente (identificada por dndId).
  // `disabledRuleIds` se mantiene por retrocompatibilidad pero se ignora.
  void disabledRuleIds;
  const computedRuleOptions = ruleOptions;

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-2 mb-3">
      {/* ── Number badge — click to edit position ── */}
      <div className="flex items-center justify-center flex-shrink-0 mt-6">
        {isEditingPosition ? (
          <input
            ref={inputRef}
            type="number"
            min={1}
            max={totalCount}
            value={positionInput}
            onChange={e => setPositionInput(e.target.value)}
            onBlur={commitPositionEdit}
            onKeyDown={e => {
              if (e.key === 'Enter') commitPositionEdit();
              if (e.key === 'Escape') cancelPositionEdit();
            }}
            className="w-9 h-9 text-center text-sm font-semibold rounded-lg border-2 border-[#568C13] bg-white text-[#568C13] focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => !readOnly && setIsEditingPosition(true)}
            disabled={readOnly}
            title={readOnly ? undefined : 'Clic para cambiar posición'}
            className={`w-9 h-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-sm font-medium text-[#475569] transition-all duration-150 ${
              readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-[#568C13] hover:text-[#568C13] hover:bg-[#F1F9EC]'
            }`}
          >
            {position}
          </button>
        )}
      </div>

      {/* ── Card ── */}
      <div className="flex-1 rounded-xl border border-[#E2E8F0] bg-white px-4 pt-3 pb-3">
        {/* Row 1: Regla + Capacidad Max + Chutes + trash */}
        <div className="flex items-end gap-4">
          {/* Rule select */}
          <div className="w-80 flex-shrink-0">
            <AppSelectSearchable
              label="Regla"
              placeholder="Seleccionar"
              isSearchable
              value={selectedRule}
              onChange={handleRuleChange}
              options={computedRuleOptions}
              disabled={readOnly}
            />
          </div>

          {/* Capacidad Max */}
          <div className="flex flex-col flex-shrink-0">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-[#475569]">Capacidad Max</span>
              <AppTooltip
                text="Define el máximo de paquetes que se asignarán a esta regla en una orden de trabajo. Al alcanzar el límite, no se asignan más paquetes."
                position="top"
              />
            </div>
            <input
              type="number"
              min={1}
              placeholder="0"
              value={capacityValue}
              onChange={handleCapacityChange}
              disabled={readOnly}
              className="mt-1 h-11 w-[105px] rounded-lg border border-[#CBD5E1] px-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-black disabled:opacity-60 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {/* Chutes — visible solo en modo sorter */}
          {showChutesSection && (
            <div className="flex flex-col items-start flex-1">
              <p className="mb-2 text-sm font-medium text-[#475569]">Chutes</p>
              <div className="flex items-center gap-[6px] flex-wrap">
                {chutesList.length > 0 ? (
                  <>
                    {chutesList.slice(0, 6).map(val => (
                      <div
                        key={val.id ?? val.chute}
                        className="rounded-md flex justify-center items-center w-[40px] h-[40px] bg-[#F1F9EC] text-sm font-medium text-[#141A1F]"
                      >
                        {val.chute}
                      </div>
                    ))}
                    {chutesList.length > 6 && (
                      <div className="rounded-md flex justify-center items-center w-[40px] h-[40px] bg-[#E2E8F0] text-sm font-medium text-gray-700">
                        +{chutesList.length - 6}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => canOpenSelector && setShowChuteModal(true)}
                      className={`border rounded-md flex justify-center items-center w-[40px] h-[40px] transition-all duration-200
                        ${canOpenSelector ? 'cursor-pointer hover:bg-[#E2E8F0]' : 'cursor-not-allowed bg-gray-100'}`}
                    >
                      <DynamicIcon name="pencil" className="h-4 w-4 text-[#475569]" />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => canOpenSelector && setShowChuteModal(true)}
                    className={`border rounded-md flex justify-center items-center w-[40px] h-[40px] transition-all duration-200
                      ${canOpenSelector ? 'cursor-pointer hover:bg-[#E2E8F0]' : 'cursor-not-allowed bg-gray-100'}`}
                  >
                    <img src={IconAdd} alt="Add chutes" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Spacer cuando no hay chutes */}
          {!showChutesSection && <div className="flex-1" />}

          {/* Trash */}
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={onDelete}
              disabled={readOnly}
              className={`border rounded-md flex justify-center items-center w-[40px] h-[40px] transition-all duration-200 ${
                readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#FEE2E2]'
              }`}
              aria-label="Eliminar regla"
            >
              <DynamicIcon name="trash" className="h-4 w-4 text-[#475569]" />
            </button>
          </div>
        </div>

        {/* Row 2: Route change */}
        <div className="mt-3">
          <RouteChangeSelector
            key={ruleIndex}
            value={routeChangeValue}
            onChange={handleRouteChange}
            disabled={readOnly}
            onLoadingChange={loading => onRouteChangeLoadingChange?.(ruleIndex, loading)}
          />
        </div>
      </div>

      {/* ── Drag handle ── */}
      <div
        {...(readOnly ? {} : { ...attributes, ...listeners })}
        className={`flex items-center justify-center w-6 flex-shrink-0 mt-1 select-none ${
          readOnly ? 'text-[#CBD5E1] cursor-not-allowed' : 'cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-[#475569]'
        }`}
        aria-label="Arrastrar para reordenar"
      >
        <svg
          width="16"
          height="24"
          viewBox="0 0 16 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="5" cy="5" r="1.5" />
          <circle cx="11" cy="5" r="1.5" />
          <circle cx="5" cy="12" r="1.5" />
          <circle cx="11" cy="12" r="1.5" />
          <circle cx="5" cy="19" r="1.5" />
          <circle cx="11" cy="19" r="1.5" />
        </svg>
      </div>

      {/* ── Chute selector modal ── */}
      {showChuteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="relative">
            <SelectChute
              chutes={chutes}
              onChange={handleChutesChange}
              onClose={() => setShowChuteModal(false)}
              disabledChutes={selectedChutes}
            />
          </div>
        </div>
      )}
    </div>
  );
};

PatternFormRuleItem.displayName = 'PatternForm.RuleItem';
export default PatternFormRuleItem;
